<script setup lang="ts">
import type { VideoCallJoinRequest } from "~/types/videoCalls";

const props = withDefaults(
  defineProps<{
    modelValue: boolean;
    shareUrl?: string;
    maxParticipants: number;
    pendingRequests: VideoCallJoinRequest[];
    atCapacity: boolean;
    loading?: boolean;
    copied?: boolean;
    errorMessage?: string;
  }>(),
  {
    shareUrl: "",
    loading: false,
    copied: false,
    errorMessage: "",
  },
);

const emit = defineEmits<{
  close: [];
  copy: [];
  rotate: [];
  approve: [requestId: string];
  deny: [requestId: string];
}>();

const titleId = useId();
const invitationInput = ref<HTMLInputElement | null>(null);
const waitingRoomHeading = ref<HTMLElement | null>(null);
const actionAnnouncement = ref("");
const invitationGenerationPending = ref(false);
const pendingRequestAction = ref<{
  id: string;
  name: string;
  action: "approve" | "deny";
} | null>(null);

const rotateInvitation = (): void => {
  invitationGenerationPending.value = true;
  actionAnnouncement.value = "";
  emit("rotate");
};

const updateRequest = (
  request: VideoCallJoinRequest,
  action: "approve" | "deny",
): void => {
  pendingRequestAction.value = {
    id: request.id,
    name: request.name,
    action,
  };
  actionAnnouncement.value = "";
  if (action === "approve") {
    emit("approve", request.id);
  } else {
    emit("deny", request.id);
  }
};

watch(
  () => props.shareUrl,
  async (shareUrl, previousShareUrl) => {
    if (
      !invitationGenerationPending.value ||
      !shareUrl ||
      shareUrl === previousShareUrl
    ) {
      return;
    }
    invitationGenerationPending.value = false;
    actionAnnouncement.value = "Link de convite gerado.";
    await nextTick();
    invitationInput.value?.focus();
  },
);

watch(
  () => props.pendingRequests.map((request) => request.id),
  async (requestIds) => {
    const pendingAction = pendingRequestAction.value;
    if (!pendingAction || requestIds.includes(pendingAction.id)) return;

    pendingRequestAction.value = null;
    actionAnnouncement.value =
      pendingAction.action === "approve"
        ? `${pendingAction.name} foi admitido na chamada.`
        : `${pendingAction.name} foi recusado.`;
    await nextTick();
    waitingRoomHeading.value?.focus();
  },
);

const closeDialog = (): void => {
  if (props.loading) return;
  invitationGenerationPending.value = false;
  pendingRequestAction.value = null;
  actionAnnouncement.value = "";
  emit("close");
};

const updateModelValue = (open: boolean): void => {
  if (!open) closeDialog();
};
</script>

<template>
  <v-dialog
    :model-value="props.modelValue"
    :persistent="props.loading"
    :z-index="2600"
    width="calc(100% - 32px)"
    max-width="680"
    scrollable
    :aria-labelledby="titleId"
    data-testid="video-call-host-tools-dialog"
    @update:model-value="updateModelValue"
  >
    <v-card class="host-tools-dialog" rounded="xl">
      <v-card-title class="host-tools-dialog__header">
        <span :id="titleId">Convite e sala de espera</span>
        <button
          type="button"
          class="host-tools-dialog__close"
          aria-label="Fechar convite e sala de espera"
          :disabled="props.loading"
          @click="closeDialog"
        >
          <i class="mdi mdi-close" aria-hidden="true" />
        </button>
      </v-card-title>

      <v-card-text class="host-tools-dialog__content">
        <p
          v-if="props.errorMessage"
          class="host-tools-dialog__error"
          role="alert"
        >
          <i class="mdi mdi-alert-circle-outline" aria-hidden="true" />
          {{ props.errorMessage }}
        </p>

        <section class="host-tools-dialog__section">
          <div class="host-tools-dialog__section-heading">
            <div>
              <span>Convite</span>
              <h3>Convide por link</h3>
            </div>
            <span class="host-tools-dialog__participant-limit">
              <i class="mdi mdi-account-multiple-outline" aria-hidden="true" />
              Até {{ props.maxParticipants }} participantes
            </span>
          </div>

          <p>
            O convidado informa apenas o nome e aguarda sua aprovação para
            entrar na chamada.
          </p>

          <div
            v-if="props.shareUrl"
            class="host-tools-dialog__invitation-link-row"
          >
            <input
              ref="invitationInput"
              type="text"
              class="host-tools-dialog__invitation-link"
              :value="props.shareUrl"
              aria-label="Link da chamada"
              readonly
              :spellcheck="false"
            />
            <button
              type="button"
              class="host-tools-dialog__outlined-button"
              :disabled="props.loading"
              @click="emit('copy')"
            >
              <i
                class="mdi"
                :class="props.copied ? 'mdi-check' : 'mdi-content-copy'"
                aria-hidden="true"
              />
              {{ props.copied ? "Link copiado" : "Copiar link" }}
            </button>
          </div>
          <button
            v-else
            type="button"
            class="host-tools-dialog__outlined-button"
            :disabled="props.loading"
            @click="rotateInvitation"
          >
            <i class="mdi mdi-link-variant-plus" aria-hidden="true" />
            Gerar link
          </button>
        </section>

        <section class="host-tools-dialog__section">
          <div class="host-tools-dialog__section-heading">
            <div>
              <span>Participantes</span>
              <h3 ref="waitingRoomHeading" tabindex="-1">Sala de espera</h3>
            </div>
            <span
              v-if="props.pendingRequests.length"
              class="host-tools-dialog__request-count"
            >
              {{ props.pendingRequests.length }}
            </span>
          </div>

          <p
            v-if="props.atCapacity && props.pendingRequests.length"
            class="host-tools-dialog__capacity"
            role="status"
          >
            Limite de quatro participantes atingido. Remova alguém da chamada
            para admitir outra pessoa.
          </p>

          <p
            v-if="!props.pendingRequests.length"
            class="host-tools-dialog__empty"
          >
            Ninguém está aguardando para entrar.
          </p>

          <div v-else class="host-tools-dialog__waiting-list">
            <article v-for="request in props.pendingRequests" :key="request.id">
              <div class="host-tools-dialog__waiting-identity">
                <span>
                  <i class="mdi mdi-account-clock-outline" aria-hidden="true" />
                  {{ request.name }}
                </span>
                <span
                  v-if="request.account_linked"
                  class="host-tools-dialog__account-badge"
                  data-testid="video-call-waiting-account-badge"
                >
                  Conta TeachPlus
                </span>
              </div>
              <div class="host-tools-dialog__waiting-actions">
                <button
                  type="button"
                  class="host-tools-dialog__text-button"
                  :disabled="props.loading"
                  :aria-label="`Recusar ${request.name}`"
                  @click="updateRequest(request, 'deny')"
                >
                  Recusar
                </button>
                <button
                  type="button"
                  class="host-tools-dialog__primary-button"
                  :disabled="props.loading || props.atCapacity"
                  :aria-label="`Admitir ${request.name}`"
                  @click="updateRequest(request, 'approve')"
                >
                  Admitir
                </button>
              </div>
            </article>
          </div>
        </section>

        <p class="host-tools-dialog__sr-only" aria-live="polite">
          {{ actionAnnouncement }}
        </p>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<style scoped lang="scss">
.host-tools-dialog {
  max-height: calc(100dvh - 32px);
  overflow: hidden;
  color: #26334d;
}

.host-tools-dialog__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 20px 22px 12px;
  color: #17233c;
  font-size: 1.1rem;
  font-weight: 700;
  white-space: normal;
}

.host-tools-dialog__close {
  display: grid;
  width: 36px;
  height: 36px;
  padding: 0;
  place-items: center;
  border: 0;
  border-radius: 10px;
  background: #f0f3f9;
  color: #273550;
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
}

.host-tools-dialog__content {
  display: grid;
  min-height: 0;
  gap: 14px;
  padding: 8px 22px 22px;
  overflow-y: auto;
}

.host-tools-dialog__error {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 0;
  padding: 10px 12px;
  border-radius: 10px;
  background: #fff0f1;
  color: #8f2231;
  font-size: 0.78rem;
  line-height: 1.4;
}

.host-tools-dialog__sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  border: 0;
  margin: -1px;
  white-space: nowrap;
}

.host-tools-dialog__section {
  display: grid;
  gap: 10px;
  padding: 14px;
  border: 1px solid #e2e7f1;
  border-radius: 14px;
  background: #f9faff;

  > p {
    margin: 0;
    color: #69758b;
    font-size: 0.78rem;
    line-height: 1.45;
  }
}

.host-tools-dialog__section-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-width: 0;
  gap: 12px;

  > div {
    display: grid;
    min-width: 0;
    gap: 2px;
  }

  span:first-child {
    color: #69758b;
    font-size: 0.68rem;
    font-weight: 700;
    letter-spacing: 0.05em;
    text-transform: uppercase;
  }

  h3 {
    margin: 0;
    color: #26334d;
    font-size: 0.95rem;
  }
}

.host-tools-dialog__participant-limit,
.host-tools-dialog__request-count {
  display: inline-flex;
  align-items: center;
  flex: 0 0 auto;
  gap: 5px;
  color: #3156ff;
  font-size: 0.7rem;
  font-weight: 700;
}

.host-tools-dialog__request-count {
  min-width: 24px;
  height: 24px;
  justify-content: center;
  border-radius: 999px;
  background: #e4eaff;
}

.host-tools-dialog__invitation-link-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  min-width: 0;
  gap: 8px;
}

.host-tools-dialog__invitation-link {
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  height: 38px;
  padding: 0 10px;
  border: 1px solid #ccd4e2;
  border-radius: 9px;
  outline: none;
  background: #fff;
  color: #4f5d75;
  font: inherit;
  font-size: 0.75rem;

  &:focus {
    border-color: #3156ff;
    box-shadow: 0 0 0 3px rgba(49, 86, 255, 0.12);
  }
}

.host-tools-dialog__outlined-button,
.host-tools-dialog__primary-button,
.host-tools-dialog__text-button {
  display: inline-flex;
  min-height: 36px;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 7px 11px;
  border-radius: 9px;
  font: inherit;
  font-size: 0.75rem;
  font-weight: 700;
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.48;
  }
}

.host-tools-dialog__outlined-button {
  justify-self: start;
  border: 1px solid #3156ff;
  background: #fff;
  color: #3156ff;
}

.host-tools-dialog__primary-button {
  border: 1px solid #3156ff;
  background: #3156ff;
  color: #fff;
}

.host-tools-dialog__text-button {
  border: 0;
  background: transparent;
  color: #5f6c84;
}

.host-tools-dialog__capacity {
  padding: 8px 9px;
  border-radius: 8px;
  background: #fff7e6;
  color: #805300 !important;
}

.host-tools-dialog__empty {
  padding: 12px;
  border: 1px dashed #ccd5e5;
  border-radius: 10px;
  background: #fff;
  text-align: center;
}

.host-tools-dialog__waiting-list {
  display: grid;
  gap: 8px;

  article {
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-width: 0;
    gap: 10px;
    padding: 10px 0 0;
    border-top: 1px solid #e2e7f1;
  }
}

.host-tools-dialog__waiting-identity {
  display: flex;
  align-items: center;
  min-width: 0;
  gap: 6px;

  > span:first-child {
    display: flex;
    align-items: center;
    min-width: 0;
    gap: 6px;
    overflow: hidden;
    font-size: 0.78rem;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.host-tools-dialog__account-badge {
  flex: 0 0 auto;
  padding: 2px 6px;
  border-radius: 999px;
  background: #e3f5eb;
  color: #176b49;
  font-size: 0.61rem;
  font-weight: 700;
  white-space: nowrap;
}

.host-tools-dialog__waiting-actions {
  display: flex;
  flex: 0 0 auto;
  gap: 4px;
}

@media (max-width: 520px) {
  .host-tools-dialog__header {
    padding: 16px 16px 10px;
  }

  .host-tools-dialog__content {
    padding: 6px 16px 16px;
  }

  .host-tools-dialog__section-heading,
  .host-tools-dialog__waiting-list article {
    align-items: stretch;
    flex-direction: column;
  }

  .host-tools-dialog__invitation-link-row {
    grid-template-columns: minmax(0, 1fr);
  }

  .host-tools-dialog__outlined-button,
  .host-tools-dialog__waiting-actions,
  .host-tools-dialog__waiting-actions button {
    width: 100%;
  }
}
</style>
